/**
 * Webhook event handler with signature verification and typed dispatch.
 */

import { verifyWebhookSignature } from '../core/webhook-utils';
import type {
  WebhookEvent,
  WebhookEventType,
  WebhookEventHandler,
  WebhookAnyHandler,
  WebhookHandlerConfig,
} from './types';

export class WebhookHandler {
  private handlers = new Map<WebhookEventType, WebhookEventHandler[]>();
  private anyHandlers: WebhookAnyHandler[] = [];
  private readonly secret: string;
  private readonly tolerance: number;

  constructor(config: WebhookHandlerConfig) {
    this.secret = config.secret;
    this.tolerance = config.tolerance ?? 300000; // 5 minutes
  }

  /**
   * Register a handler for a specific event type.
   */
  on<T extends WebhookEventType>(
    eventType: T,
    handler: WebhookEventHandler<T>
  ): this {
    const existing = this.handlers.get(eventType) || [];
    existing.push(handler as unknown as WebhookEventHandler);
    this.handlers.set(eventType, existing);
    return this;
  }

  /**
   * Register a catch-all handler for all event types.
   */
  onAny(handler: WebhookAnyHandler): this {
    this.anyHandlers.push(handler);
    return this;
  }

  /**
   * Verify signature and parse the webhook body.
   */
  async verifyAndParse(body: string, signature: string): Promise<WebhookEvent> {
    const isValid = await verifyWebhookSignature(body, signature, this.secret);
    if (!isValid) {
      throw new Error('Invalid webhook signature');
    }

    return this.parseAndValidate(body);
  }

  /**
   * Verify signature, parse, and dispatch to registered handlers.
   */
  async handle(body: string, signature: string): Promise<void> {
    const event = await this.verifyAndParse(body, signature);
    await this.dispatch(event);
  }

  /**
   * Parse an event without signature verification (for testing).
   */
  parseEvent(body: string): WebhookEvent {
    return this.parseAndValidate(body);
  }

  private parseAndValidate(body: string): WebhookEvent {
    const event = JSON.parse(body) as WebhookEvent;

    if (!event.type || !event.id || !event.timestamp) {
      throw new Error('Invalid webhook event: missing required fields (type, id, timestamp)');
    }

    // Timestamp tolerance check
    if (this.tolerance > 0) {
      const eventTime = new Date(event.timestamp).getTime();
      const now = Date.now();
      if (Math.abs(now - eventTime) > this.tolerance) {
        throw new Error(
          `Webhook event timestamp is outside tolerance window (${this.tolerance}ms)`
        );
      }
    }

    return event;
  }

  private async dispatch(event: WebhookEvent): Promise<void> {
    const typeHandlers = this.handlers.get(event.type) || [];
    const allHandlers = [...typeHandlers, ...this.anyHandlers];

    for (const handler of allHandlers) {
      await (handler as WebhookAnyHandler)(event);
    }
  }
}
